import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BuildIcon from '@mui/icons-material/Build';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SearchIcon from '@mui/icons-material/Search';
import { truckService } from '../api/api';
import ConfirmDialog from './ConfirmDialog';

const emptyForm = {
  name: '',
  category: '',
  expected_service_life_km: '',
  expected_service_life_years: '',
  email_enabled: false,
  email_recipients: '',
  active: true,
  vehicle_ids: [],
};

function formatKm(value) {
  const parsed = Number(value || 0);
  return parsed > 0 ? `${parsed.toLocaleString()} km` : 'Not set';
}

function monthsToYears(value) {
  const months = Number(value || 0);
  return months > 0 ? Number((months / 12).toFixed(2)) : '';
}

function yearsToMonths(value) {
  const years = parseFloat(String(value || '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(years) && years > 0 ? Math.round(years * 12) : null;
}

function kmInputValue(value) {
  return String(value ?? '').replace(/[^0-9]/g, '');
}

function yearsInputValue(value) {
  const cleaned = String(value ?? '').replace(/[^0-9.]/g, '');
  const [whole, ...rest] = cleaned.split('.');
  return rest.length ? `${whole}.${rest.join('')}` : whole;
}

function formatYearsFromMonths(value) {
  const months = Number(value || 0);
  if (months <= 0) return null;
  const years = months / 12;
  return `${Number.isInteger(years) ? years : Number(years.toFixed(2))} year${years === 1 ? '' : 's'}`;
}

function formatReminderAfter(km, months) {
  const pieces = [];
  if (Number(km || 0) > 0) pieces.push(formatKm(km));
  const yearsText = formatYearsFromMonths(months);
  if (yearsText) pieces.push(yearsText);
  return pieces.length ? pieces.join(' or ') : 'Not set';
}

function vehicleLabel(vehicle) {
  return vehicle?.name || `${vehicle?.make || ''} ${vehicle?.model || ''}`.trim() || `Vehicle ${vehicle?.id}`;
}

export default function MaintenanceReminders() {
  const [reminders, setReminders] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteReminder, setDeleteReminder] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });

  const loadData = async () => {
    try {
      setLoading(true);
      const [reminderRes, vehicleRes] = await Promise.all([
        truckService.getConsumableMaster({ includeInactive: 1 }),
        truckService.getAll({ includeArchived: 1 }),
      ]);
      const vehicleRows = vehicleRes.data?.data || vehicleRes.data || [];
      setReminders(reminderRes.data || []);
      setVehicles(Array.isArray(vehicleRows) ? vehicleRows.filter((vehicle) => !vehicle.archived) : []);
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.error || 'Failed to load scheduled maintenance reminders.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const vehicleById = useMemo(() => {
    return new Map(vehicles.map((vehicle) => [vehicle.id, vehicle]));
  }, [vehicles]);

  const filteredReminders = useMemo(() => {
    const query = search.trim().toLowerCase();
    return reminders.filter((reminder) => {
      if (!query) return true;
      return (
        reminder.name?.toLowerCase().includes(query) ||
        reminder.category?.toLowerCase().includes(query) ||
        (reminder.vehicle_ids || []).some((id) => vehicleLabel(vehicleById.get(id)).toLowerCase().includes(query))
      );
    });
  }, [reminders, search, vehicleById]);

  const filteredVehicles = useMemo(() => {
    const query = vehicleSearch.trim().toLowerCase();
    return vehicles.filter((vehicle) => {
      if (!query) return true;
      return [vehicleLabel(vehicle), vehicle.make, vehicle.model, vehicle.license_plate, vehicle.plate]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [vehicleSearch, vehicles]);

  const openAdd = () => {
    setEditingReminder(null);
    setForm(emptyForm);
    setVehicleSearch('');
    setDialogOpen(true);
  };

  const openEdit = (reminder) => {
    setEditingReminder(reminder);
    setForm({
      name: reminder.name || '',
      category: reminder.category || '',
      expected_service_life_km: reminder.expected_service_life_km ?? '',
      expected_service_life_years: monthsToYears(reminder.expected_service_life_months),
      email_enabled: reminder.email_enabled === true,
      email_recipients: reminder.email_recipients || '',
      active: reminder.active !== false,
      vehicle_ids: reminder.vehicle_ids || [],
    });
    setVehicleSearch('');
    setDialogOpen(true);
  };

  const toggleVehicle = (vehicleId) => {
    setForm((prev) => {
      const selected = new Set(prev.vehicle_ids || []);
      if (selected.has(vehicleId)) selected.delete(vehicleId);
      else selected.add(vehicleId);
      return { ...prev, vehicle_ids: Array.from(selected) };
    });
  };

  const saveReminder = async () => {
    if (!form.name.trim()) {
      setSnackbar({ open: true, severity: 'error', message: 'Reminder name is required.' });
      return;
    }
    if (!form.category.trim()) {
      setSnackbar({ open: true, severity: 'error', message: 'Category is required.' });
      return;
    }

    const reminderKm = Number(String(form.expected_service_life_km || '').replace(/[^0-9.]/g, '')) || 0;
    const reminderMonths = yearsToMonths(form.expected_service_life_years) || 0;

    if (reminderKm <= 0 && !reminderMonths) {
      setSnackbar({ open: true, severity: 'error', message: 'Set Reminder After by odometer km, years, or both.' });
      return;
    }
    if ((form.vehicle_ids || []).length === 0) {
      setSnackbar({ open: true, severity: 'error', message: 'Select at least one vehicle.' });
      return;
    }

    const payload = {
      name: form.name.trim(),
      category: form.category.trim(),
      expected_service_life_km: reminderKm > 0 ? reminderKm : 0,
      expected_service_life_months: reminderMonths > 0 ? reminderMonths : 0,
      email_enabled: form.email_enabled,
      email_recipients: form.email_enabled ? form.email_recipients : '',
      active: form.active,
      vehicle_ids: form.vehicle_ids,
    };
    
    try {
      setSaving(true);
      if (editingReminder?.id) {
        await truckService.updateConsumableMaster(editingReminder.id, payload);
      } else {
        await truckService.createConsumableMaster(payload);
      }
      setDialogOpen(false);
      setEditingReminder(null);
      await loadData();
      window.dispatchEvent(new Event('remindersUpdated'));
      window.dispatchEvent(new Event('dataUpdated'));
      setSnackbar({ open: true, severity: 'success', message: 'Scheduled maintenance reminder saved.' });
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.error || 'Failed to save reminder.',
      });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteReminder?.id) return;
    try {
      setDeleting(true);
      await truckService.deleteConsumableMaster(deleteReminder.id);
      setDeleteReminder(null);
      await loadData();
      window.dispatchEvent(new Event('remindersUpdated'));
      window.dispatchEvent(new Event('dataUpdated'));
      setSnackbar({ open: true, severity: 'success', message: 'Reminder deleted from database.' });
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.error || 'Failed to delete reminder.',
      });
    } finally {
      setDeleting(false);
    }
  };

  const selectedCount = (form.vehicle_ids || []).length;
  const allVehicleIds = vehicles.map((vehicle) => vehicle.id);

  return (
    <Box>
      <Card className="portal-page-header" sx={{ borderRadius: 2.25, boxShadow: 2, mb: 1.25 }}>
        <CardContent sx={{ p: { xs: 1.15, sm: 1.35, md: 1.55 }, '&:last-child': { pb: { xs: 1.15, sm: 1.35, md: 1.55 } } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: { xs: 1, sm: 1.25 }, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
              <Box sx={{ width: { xs: 40, sm: 46 }, height: { xs: 40, sm: 46 }, borderRadius: 2, bgcolor: '#001f3f', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <BuildIcon sx={{ fontSize: { xs: 23, sm: 27 } }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 900, color: '#001f3f', fontSize: { xs: '1rem', sm: '1.18rem', md: '1.3rem' }, lineHeight: 1.05, mb: 0.1 }}>
                  Scheduled Maintenance Reminders
                </Typography>
                <Typography color="text.secondary" sx={{ fontWeight: 700, fontSize: { xs: '0.68rem', sm: '0.76rem' }, lineHeight: 1.25 }}>
                  Create reminder templates and apply them to selected vehicles.
                </Typography>
              </Box>
            </Box>
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openAdd} sx={{ bgcolor: '#001f3f', fontWeight: 900, px: 1.55, py: 0.62, minHeight: 36, borderRadius: 1.6, fontSize: { xs: '0.78rem', sm: '0.86rem' } }}>
              Add Reminder
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, border: '1px solid #d8e2ef' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by reminder, category, or assigned vehicle"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 2, overflowX: 'auto' }}>
        <Table sx={{ minWidth: 1080 }}>
          <TableHead>
            <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: '#001f3f', color: 'white', fontWeight: 900 } }}>
              <TableCell>Reminder</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="right">Reminder After</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Applied Vehicles</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredReminders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  No scheduled maintenance reminders found.
                </TableCell>
              </TableRow>
            ) : filteredReminders.map((reminder) => {
              const assignedVehicles = (reminder.vehicle_ids || []).map((id) => vehicleById.get(id)).filter(Boolean);
              return (
                <TableRow key={reminder.id} hover>
                  <TableCell sx={{ fontWeight: 900, color: '#001f3f' }}>{reminder.name}</TableCell>
                  <TableCell>{reminder.category}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>{formatReminderAfter(reminder.expected_service_life_km, reminder.expected_service_life_months)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={reminder.email_enabled ? 'info' : 'default'}
                      label={reminder.email_enabled ? 'Email On' : 'Email Off'}
                      sx={{ fontWeight: 800 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                      {assignedVehicles.slice(0, 5).map((vehicle) => (
                        <Chip key={vehicle.id} size="small" icon={<LocalShippingIcon />} label={vehicleLabel(vehicle)} sx={{ fontWeight: 700 }} />
                      ))}
                      {assignedVehicles.length > 5 && <Chip size="small" label={`+${assignedVehicles.length - 5} more`} />}
                      {assignedVehicles.length === 0 && <Chip size="small" color="warning" label="No vehicles selected" />}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip size="small" color={reminder.active === false ? 'default' : 'success'} label={reminder.active === false ? 'Inactive' : 'Active'} sx={{ fontWeight: 900 }} />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit reminder">
                      <IconButton size="small" onClick={() => openEdit(reminder)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete reminder">
                      <IconButton size="small" color="error" onClick={() => setDeleteReminder(reminder)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 900, color: '#001f3f' }}>
          {editingReminder ? 'Edit Scheduled Reminder' : 'Add Scheduled Reminder'}
          <IconButton onClick={() => setDialogOpen(false)} sx={{ position: 'absolute', right: 12, top: 10 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Reminder Name" value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Category" value={form.category} onChange={(event) => setForm((prev) => ({ ...prev, category: event.target.value }))} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Reminder After (km)"
                value={form.expected_service_life_km}
                onChange={(event) => setForm((prev) => ({ ...prev, expected_service_life_km: kmInputValue(event.target.value) }))}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                helperText="Odometer based"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Reminder After (years)"
                value={form.expected_service_life_years}
                onChange={(event) => setForm((prev) => ({ ...prev, expected_service_life_years: yearsInputValue(event.target.value) }))}
                inputProps={{ inputMode: 'decimal' }}
                helperText="Time based"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.active} onChange={(event) => setForm((prev) => ({ ...prev, active: event.target.checked }))} />}
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc' }}>
                <FormControlLabel
                  control={<Switch checked={form.email_enabled} onChange={(event) => setForm((prev) => ({ ...prev, email_enabled: event.target.checked }))} />}
                  label={<Typography sx={{ fontWeight: 900, color: '#001f3f' }}>Send Email When This Reminder Is Due</Typography>}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Email Recipients"
                  value={form.email_recipients}
                  onChange={(event) => setForm((prev) => ({ ...prev, email_recipients: event.target.value }))}
                  disabled={!form.email_enabled}
                  placeholder="dane@example.com, vu@example.com"
                  helperText="Comma, semicolon, or new-line separated email addresses."
                  sx={{ mt: 1 }}
                />
              </Paper>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.5 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
            <Box>
              <Typography sx={{ fontWeight: 900, color: '#001f3f' }}>Apply To Vehicles</Typography>
              <Typography variant="body2" color="text.secondary">{selectedCount} of {vehicles.length} vehicles selected</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button size="small" variant="outlined" onClick={() => setForm((prev) => ({ ...prev, vehicle_ids: allVehicleIds }))}>Select All</Button>
              <Button size="small" variant="outlined" color="inherit" onClick={() => setForm((prev) => ({ ...prev, vehicle_ids: [] }))}>Clear</Button>
            </Box>
          </Box>

          <TextField
            fullWidth
            size="small"
            placeholder="Search vehicles"
            value={vehicleSearch}
            onChange={(event) => setVehicleSearch(event.target.value)}
            sx={{ mb: 1.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Paper variant="outlined" sx={{ maxHeight: 320, overflow: 'auto', p: 1, borderRadius: 2 }}>
            <Grid container spacing={1}>
              {filteredVehicles.map((vehicle) => (
                <Grid item xs={12} sm={6} md={4} key={vehicle.id}>
                  <Box
                    onClick={() => toggleVehicle(vehicle.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      borderRadius: 2,
                      cursor: 'pointer',
                      border: (form.vehicle_ids || []).includes(vehicle.id) ? '1px solid #1976d2' : '1px solid #e0e7ef',
                      bgcolor: (form.vehicle_ids || []).includes(vehicle.id) ? 'rgba(25,118,210,0.08)' : 'white',
                    }}
                  >
                    <Checkbox checked={(form.vehicle_ids || []).includes(vehicle.id)} size="small" />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 900, color: '#001f3f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {vehicleLabel(vehicle)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[vehicle.make, vehicle.model, vehicle.license_plate || vehicle.plate].filter(Boolean).join(' • ') || `ID ${vehicle.id}`}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
              {filteredVehicles.length === 0 && (
                <Grid item xs={12}>
                  <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>No vehicles found.</Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={saving} onClick={saveReminder} sx={{ bgcolor: '#001f3f', fontWeight: 900 }}>
            {saving ? 'Saving…' : 'Save Reminder'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteReminder)}
        title="Delete Reminder?"
        description={`This will permanently delete "${deleteReminder?.name || 'this reminder'}" from the database, including its vehicle assignments and replacement history.`}
        confirmText="Delete"
        severity="error"
        loading={deleting}
        onConfirm={confirmDelete}
        onClose={() => { if (!deleting) setDeleteReminder(null); }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
